import axios from "axios";
import { filterMigratedExamChapters } from "../data/examBlock";

// API 配置
interface ApiConfig {
  baseURL: string;
  authToken: string;
  schoolSystem: string;
  subject: string;
}

// 國文批改系統配置
const apiConfig: ApiConfig = {
  baseURL: import.meta.env.VITE_API_URL || '',
  authToken: import.meta.env.VITE_API_AUTH_TOKEN || '',
  schoolSystem: "高中",
  subject: "國文" // ✅ 已切換為國文批改系統
};

// 創建 axios 實例
const api = axios.create({
  baseURL: apiConfig.baseURL,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${apiConfig.authToken}`,
  },
  timeout: 1800000, // 1800秒超時
});

// 請求攔截器
api.interceptors.request.use(
  (config) => {
    // API Request Debug removed to reduce log noise
    
    // 強制確保 Authorization header 存在
    if (apiConfig.authToken && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${apiConfig.authToken}`;
      console.log('🔧 手動添加 Authorization header:', config.headers.Authorization);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// 回應攔截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ Response Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data,
      timestamp: new Date().toISOString()
    });
    return Promise.reject(error);
  }
);

// API 函數開始

const getBooks = () => {
  const params = new URLSearchParams({
    schoolSystem: apiConfig.schoolSystem,
    subject: apiConfig.subject,
  });
  return api.get("/books/", { params });
};

const getChapters = (bookId: string, sub?: string) => {
  const req = sub
    ? api.get(`/bookchapterinfo/${bookId}?studentId=${sub}`)
    : api.get(`/bookchapterinfo/${bookId}`);
  // 全站唯一章節入口:在此過濾舊書被資料同步復活的考題章節(見 examBlock.ts)
  return req.then((res) => {
    if (Array.isArray(res.data)) {
      res.data = filterMigratedExamChapters(bookId, res.data);
    }
    return res;
  });
};

const getBookRecords = (bookId: string, studentId?: string, classCode?: string) => {
  // 修正：移除尾隨斜線，/record/chinese 而不是 /record/chinese/
  return api.get("/record/chinese", {
    params: {
      bookId,
      studentId,
      classCode,
    },
  });
};

const getStudentClasses = (studentId: string) => {
  const params = new URLSearchParams({
    studentId,
    schoolSystem: apiConfig.schoolSystem,
    subject: apiConfig.subject,
  });
  return api.get("/class/", { params });
};

const getTeacherClasses = (teacherId: string) => {
  return api.get(`/invition/${teacherId}`);
};

const getClassRecords = (classCode: string, bookId?: string) => {
  // bookId:學測/模考專區沿用舊班級,需覆寫班級推導的書(kanban 107204)
  return api.get(`/classrecord/chinese/${classCode}`, {
    params: bookId ? { bookId } : undefined,
  });
};

const getStudents = (classCode: string) => {
  return api.get(`/class/${classCode}`);
};

// 老師手動調整AI批改結果(kanban 106732)
// 部分更新:只送要改的欄位;後端會驗證 teacherId 是否為該紀錄班級的老師
interface TeacherCategoryEdit {
  得分?: number;
  評語?: string;
}

interface TeacherRecordEditPayload {
  teacherId: string;
  整體評分?: {
    總分?: number;
    評語?: string;
    建議?: string[];
  };
  categories?: Record<string, TeacherCategoryEdit>;
}

const updateRecordCorrection = (recordId: string, payload: TeacherRecordEditPayload) => {
  return api.put(`/record/chinese/${recordId}`, JSON.stringify(payload));
};

// 刪除已完成的批改紀錄(kanban 106957 補做)。
// 老師要重新批改某位學生時得先刪舊紀錄 —— 批次後端以 (classID, compositionID, seatNo)
// 判定重複,舊紀錄還在就靜默忽略新的上傳(同國中批次紀錄頁的刪除動線)。
// base/header 與 updateRecordCorrection 同一組;後端只放行 batch_ 開頭的紀錄,
// 且會驗 teacherId(老師 OIDC sub)是否為該紀錄班級的老師。
// 失敗一律 { detail }:403(非本班老師/非 batch_ 紀錄)、404(紀錄不存在)、422(缺 teacherId)
const deleteClassRecord = (recordId: string, teacherId: string) => {
  return api.delete(`/record/chinese/${recordId}`, {
    params: { teacherId },
  });
};

const updateStudent = (
  _id: string,
  classCode: string,
  studentID: string,
  studentName: string,
  studentEmail: string,
  studentSeatNo: string,
  studentSeatNoInt: number
) => {
  const data = {
    _id,
    classCode,
    studentID,
    studentName,
    studentEmail,
    studentSeatNo,
    studentSeatNoInt,
  };
  return api.put(`/class/`, JSON.stringify(data));
};

const joinClass = (data: {
  _id: string;
  classCode: string;
  studentID: string;
  studentEmail: string;
  studentName: string;
  studentSeatNo: string;
  studentSeatNoInt: number;
}) => {
  return api.post(`/class/`, JSON.stringify(data));
};

const aiCorrecting = (
  stuId: string,
  bookId: string,
  chaterId: string,
  stuName: string,
  stuEmail: string,
  classCode: string,
  className: string,
  studentInput?: string,
  files?: File | File[],  // 分割後的圖片（原本的files）
  originFiles?: File | File[]  // 新增：校正後的圖片（originFiles）
) => {
  const data = new FormData();
  data.append("studentId", stuId);
  data.append("bookId", bookId);
  data.append("chapterId", chaterId);
  data.append("studentName", stuName);
  data.append("studentEmail", stuEmail);
  data.append("classCode", classCode);
  data.append("className", className);
  data.append("studentSeatNo", "0");
  data.append("studentSeatNoInt", "0");
  
  if (studentInput) {
    data.append("studentInput", studentInput);
  }
  
  // 處理分割後的檔案上傳（files）
  if (files) {
    if (Array.isArray(files)) {
      // 多個檔案：每個都使用 'files' 作為欄位名稱
      files.forEach((file) => {
        data.append("files", file);
      });
    } else {
      // 單個檔案
      data.append("files", files);
    }
  }
  
  // 處理校正後的檔案上傳（originFiles）
  if (originFiles) {
    if (Array.isArray(originFiles)) {
      // 多個檔案：每個都使用 'originFiles' 作為欄位名稱
      originFiles.forEach((file) => {
        data.append("originFiles", file);
      });
    } else {
      // 單個檔案
      data.append("originFiles", originFiles);
    }
  }
  
  return api.post("/aicorrectingticket", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// 獲取批改狀態 API
const getCorrectionStatus = (studentId: string) => {
  return api.get("/aicorrectingticket", {
    params: { studentId }
  });
};

// 刪除批改 ticket API
const deleteCorrectionTicket = (ticket: string) => {
  return api.delete("/aicorrectingticket", {
    params: { ticket }
  });
};

const createClass = (
  _id: string,
  teacherID: string,
  teacherEmail: string,
  teacherName: string,
  className: string,
  classCode: string
) => {
  const data = {
    _id,
    teacherID,
    teacherEmail,
    teacherName,
    teacherSchool: apiConfig.schoolSystem,
    className,
    classCode,
  };
  return api.post("invition/", JSON.stringify(data));
};

// 新版建立班級 API（含 bookID）
// 若書籍 isNeedInvition === "Y"，需帶入 activationCode（業務發放的開通碼）作為 _id
const createBookClass = (
  bookID: string,
  teacherID: string,
  teacherEmail: string,
  teacherName: string,
  className: string,
  classCode: string,
  activationCode?: string
) => {
  const data: Record<string, string> = {
    bookID,
    teacherID,
    teacherEmail,
    teacherName,
    teacherSchool: apiConfig.schoolSystem,
    className,
    classCode,
  };
  if (activationCode) {
    data._id = activationCode;
  }
  return api.post("chinese/invition/", JSON.stringify(data));
};

const updateClass = (
  _id: string,
  teacherID: string,
  teacherEmail: string,
  teacherName: string,
  className: string,
  classCode: string
) => {
  const data = {
    _id,
    teacherID,
    teacherEmail,
    teacherName,
    teacherSchool: apiConfig.schoolSystem,
    className,
    classCode,
  };
  return api.put("invition/", JSON.stringify(data));
};

// 刪除班級
const deleteClass = (classCode: string) => {
  return api.delete("invition", {
    params: { classCode }
  });
};

// 批改記錄相關介面
interface CorrectionRecord {
  userID: string;
  input: string;
  output: string;
  schoolSystem: string;
  serviceType: string;
  totalScore: number;
  isDownloaded: boolean;
}

interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    [key: string]: unknown;
  };
}

// 發送批改記錄到 API
const sendCorrectionRecord = async (
  recordData: CorrectionRecord
): Promise<ApiResult> => {
  try {
    const response = await api.post('/correction-record/', recordData);
    return {
      success: true,
      data: response.data
    };
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { detail?: string; message?: string } } };
    return {
      success: false,
      error: {
        message: axiosError.response?.data?.detail || axiosError.response?.data?.message || '發送批改記錄失敗'
      }
    };
  }
};

// 更新下載狀態
const updateDownloadStatus = async (
  recordId: string
): Promise<ApiResult> => {
  try {
    const response = await api.patch(`/correction-record/${recordId}/download`, {
      isDownloaded: true
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error: unknown) {
    const axiosError = error as { response?: { data?: { detail?: string; message?: string } } };
    return {
      success: false,
      error: {
        message: axiosError.response?.data?.detail || axiosError.response?.data?.message || '更新下載狀態失敗'
      }
    };
  }
};

// 獲取當前環境
const getCurrentEnvironment = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return 'development';
    } else if (hostname.includes('staging') || hostname.includes('qa')) {
      return 'staging';
    } else {
      return 'production';
    }
  }
  
  return 'development';
};

// 獲取當前 API 配置（用於調試）
const getApiConfig = (): ApiConfig => {
  return { ...apiConfig };
};

// 更新 API 配置（用於切換科目）
const updateApiConfig = (newConfig: Partial<ApiConfig>): void => {
  Object.assign(apiConfig, newConfig);
};

// ── correcting-log（自助查看儀表板 /viewer 的資料來源）─────────────────────────
// 注意：這跟高中自家後端的 `/correction-record/`（sendCorrectionRecord）是兩回事。
// correcting-log 是跨學段共用的批改紀錄庫，國中（chaiwriting-j）與批次都會把每篇批改
// 鏡一份進來，儀表板才看得到。高中 2025-07 改版成 Vite 時漏接此寫入，導致高中資料自此
// 在儀表板全空（歷史 1002 筆仍在，但無新增）→ 此處補回。
//
// URL 走 build-time env `VITE_LOG_API_URL`（由 deploy workflow 注入：QA→qa-、prod→正式）；
// 未設時依 hostname fallback（localhost/qa → QA，其餘 → prod），避免本機開發誤寫 prod。
const LOG_API_ENDPOINTS = {
  development: "https://qa-correcting-log-api.hle.com.tw/data",
  production: "https://correcting-log-api.hle.com.tw/data",
} as const;

const resolveLogApiUrl = (): string => {
  const injected = import.meta.env.VITE_LOG_API_URL as string | undefined;
  if (injected) return injected;
  return getCurrentEnvironment() === "production"
    ? LOG_API_ENDPOINTS.production
    : LOG_API_ENDPOINTS.development;
};

interface CorrectionLogData {
  userID: string;
  input: string;
  output: string;
  totalScore: number | string;
  picurl?: string;
  serviceType?: string;
}

// 寫一份批改紀錄到 correcting-log（儀表板資料來源）。fire-and-forget：
// 失敗只記 console，不可影響批改結果頁顯示。
const sendCorrectionLog = async (
  data: CorrectionLogData
): Promise<ApiResult> => {
  const payload: Record<string, unknown> = {
    userID: data.userID || "",
    input: data.input || "",
    output: data.output || "",
    schoolSystem: apiConfig.schoolSystem, // "高中"
    serviceType: data.serviceType || "作文批改",
    totalScore: data.totalScore || 0,
    isDownloaded: false,
    isCommentCopied: false,
  };
  if (data.picurl) payload.picurl = data.picurl;

  try {
    const response = await fetch(resolveLogApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    try {
      return { success: true, data: JSON.parse(text) };
    } catch {
      return { success: true, data: text };
    }
  } catch (error: unknown) {
    return {
      success: false,
      error: { message: (error as Error)?.message || "寫入 correcting-log 失敗" },
    };
  }
};

export {
  api,
  getBooks,
  getChapters,
  getBookRecords,
  aiCorrecting,
  getCorrectionStatus,
  deleteCorrectionTicket,
  joinClass,
  getStudentClasses,
  getTeacherClasses,
  getClassRecords,
  updateClass,
  deleteClass,
  getStudents,
  updateStudent,
  updateRecordCorrection,
  deleteClassRecord,
  createClass,
  createBookClass,
  sendCorrectionRecord,
  sendCorrectionLog,
  updateDownloadStatus,
  getCurrentEnvironment,
  getApiConfig,
  updateApiConfig,
};

// 匯出類型定義
export type { ApiConfig, CorrectionRecord, ApiResult, TeacherRecordEditPayload, TeacherCategoryEdit };